import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import TableView from "../components/TableView";
import AccessoryTableAction from "../generatedTableActions/AccessoryTableAction";
import Badge from "../components/Badge";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  setCurrentPage,
  setPerPage,
  setLoading,
  setAccessory,
  setSearchTerm,
  setTotalPage,
  setTotalCount,
  
} from "../generatedSlice/accessorySlice";
import { getAccessory } from "../generatedRequest/accessory";
import { toast } from "react-toastify";
import TableSkeleton from "../components/TableSkeleton";
import AccessoryList from "../generatedTableList/AccessoryList";
import { TTableProps } from "../types/TableView";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TOptionType } from "../types/CustomSelect";
import AccessoryFilters from "../generatedFilters/AccessoryFilters";
import { getRegion } from "../app/request/regionRequest";
import UploadAccessoryFormModal from "../generatedFormModal/UploadAccessoryFormModal";
import { ActionCells } from "../components/ActionCells";
import { toAbsoluteUrl } from "../_metronic/helpers";
import { debounce } from 'lodash';
import PermissionPrompt from "../components/PermissionPrompt";

const actions = ["Manually", "Upload-Excel"];

const FILTER_STORAGE_KEY = 'accessory_filters';
const NAVIGATION_STORAGE_KEY = 'accessory_navigation';

const AccessoryPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialLoadRef = useRef(true);
  const isResettingRef = useRef(false);
  const isInitialMount = useRef(true);

  const isFromEditPage = searchParams.toString().length > 0;

  // Permission states
  const [showPermissionPrompt, setShowPermissionPrompt] = useState<boolean>(false);
const [userPermissions, setUserPermissions] = useState({ canAdd: false });
  // Get user permissions from localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const userDataString = localStorage.getItem('kt-auth-react-v');

        if (!userDataString) {
          // console.warn('kt-auth-react-v not found in localStorage');
          setUserPermissions({ canAdd: false });
          return;
        }

        const userData = JSON.parse(userDataString);
        // console.log('User Data:', userData);

        const accessoryPermissions = userData?.data?.permissions?.find(
          (permission: any) => permission.moduleName === "Accessory"
        );

        // console.log('Accessory Permissions:', accessoryPermissions);

        if (accessoryPermissions) {
          const addActivity = accessoryPermissions.moduleActivities.find(
            (activity: any) => activity.activityFunction === "add"
          );

          // console.log('Add Activity:', addActivity);

          const canAdd = addActivity?.isSelected || false;

          // console.log('Permissions - canAdd:', canAdd);

          setUserPermissions({ canAdd });
        } else {
          setUserPermissions({ canAdd: false });
        }
      } catch (error) {
        console.error("Error parsing user permissions:", error);
        setUserPermissions({ canAdd: false });
      }
    }, 300);


    return () => clearTimeout(timer);
  }, []);
  const checkBackButtonNavigation = () => {
    const navData = localStorage.getItem(NAVIGATION_STORAGE_KEY);
    if (navData) {
      try {
        const parsedData = JSON.parse(navData);
        const isRecentNavigation = (Date.now() - parsedData.timestamp) < 5 * 60 * 1000;
        return parsedData.fromBackButton && isRecentNavigation;
      } catch (error) {
        console.error('Error parsing navigation data:', error);
        return false;
      }
    }
    return false;
  };

  const isFromBackButton = checkBackButtonNavigation();

  const [filters, setFilters] = useState({
    status: { label: "", value: "" },
    region: "",
    category: "",
    subcategory: "",
    product: "",
    brand: "",
    initialized: false,
    fromUrl: false,
    fromLocalStorage: false,
  });

  const [filtersReady, setFiltersReady] = useState(false);
  const [allNewRegion, setAllNewRegion] = useState([]);
  const [initload, setInitload] = useState(true);
  const [filterloading, setFilterLoading] = useState(false);
  const [uploadExcel, setUploadExcel] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const {
    data,
    perPage,
    currentPage,
    totalPage,
    searchTerm,
    loading,
    sortBy,
    sortColumn,
    rerender1,
    totalCount
  } = useSelector((state: RootState) => state.accessory);

  // Check if any filters or search are applied
  const hasActiveFilters = useMemo(() => {
    const hasSearch = searchTerm && searchTerm.trim() !== '';
    const hasStatus = filters.status?.value && filters.status.value !== '';
    const hasRegion = filters.region && filters.region !== '';
    const hasCategory = filters.category && filters.category !== '';
    const hasSubcategory = filters.subcategory && filters.subcategory !== '';
    const hasProduct = filters.product && filters.product !== '';
    const hasBrand = filters.brand && filters.brand !== '';
    const hasPageChange = currentPage !== 1;
    const hasPerPageChange = perPage !== 10;

    return hasSearch || hasStatus || hasRegion || hasCategory || hasSubcategory || hasProduct || hasBrand || hasPageChange || hasPerPageChange;
  }, [searchTerm, filters, currentPage, perPage]);

  const saveFiltersToLocalStorage = useCallback((filtersData: any) => {
    try {
      const filterDataToSave = {
        status: filtersData.status,
        region: filtersData.region,
        category: filtersData.category,
        subcategory: filtersData.subcategory,
        product: filtersData.product,
        brand: filtersData.brand,
        searchTerm: searchTerm,
        currentPage: currentPage,
        perPage: perPage,
        timestamp: Date.now(),
      };
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterDataToSave));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [searchTerm, currentPage, perPage]);

  const loadFiltersFromLocalStorage = useCallback(() => {
    try {
      const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        const isDataFresh = (Date.now() - parsedFilters.timestamp) < 60 * 60 * 1000;
        if (isDataFresh) {
          return parsedFilters;
        } else {
          localStorage.removeItem(FILTER_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    return null;
  }, []);

  const clearNavigationData = useCallback(() => {
    localStorage.removeItem(NAVIGATION_STORAGE_KEY);
  }, []);

  // Synchronous filter initialization
  if (!filters.initialized) {
    if (isFromBackButton) {
      const savedFilters = loadFiltersFromLocalStorage();
      if (savedFilters) {
        if (savedFilters.searchTerm) {
          dispatch(setSearchTerm(savedFilters.searchTerm));
        }
        if (savedFilters.currentPage) {
          dispatch(setCurrentPage(savedFilters.currentPage));
        }
        if (savedFilters.perPage) {
          dispatch(setPerPage(savedFilters.perPage));
        }

        setFilters({
          status: savedFilters.status || { label: "", value: "" },
          region: savedFilters.region || "",
          category: savedFilters.category || "",
          subcategory: savedFilters.subcategory || "",
          product: savedFilters.product || "",
          brand: savedFilters.brand || "",
          initialized: true,
          fromUrl: false,
          fromLocalStorage: true,
        });

        setFiltersReady(true);
        clearNavigationData();
      } else {
        setFilters(prev => ({ ...prev, initialized: true, fromLocalStorage: false }));
        setFiltersReady(true);
      }
    } else if (isFromEditPage) {
      const urlStatus = searchParams.get('status');
      const urlRegion = searchParams.get('region');
      const urlCategory = searchParams.get('category');
      const urlSubcategory = searchParams.get('subcategory');
      const urlProduct = searchParams.get('productId');
      const urlBrand = searchParams.get('brand');
      const urlSearch = searchParams.get('search');
      const urlPage = searchParams.get('page');
      const urlLimit = searchParams.get('limit');

      if (urlSearch) {
        dispatch(setSearchTerm(urlSearch));
      }
      if (urlPage) {
        dispatch(setCurrentPage(parseInt(urlPage)));
      }
      if (urlLimit) {
        dispatch(setPerPage(parseInt(urlLimit)));
      }

      setFilters({
        status: urlStatus ? {
          label: urlStatus.charAt(0).toUpperCase() + urlStatus.slice(1),
          value: urlStatus,
        } : { label: "", value: "" },
        region: urlRegion || "",
        category: urlCategory || "",
        subcategory: urlSubcategory || "",
        product: urlProduct || "",
        brand: urlBrand || "",
        initialized: true,
        fromUrl: true,
        fromLocalStorage: false,
      });

      setFiltersReady(true);
    } else {
      const savedFilters = loadFiltersFromLocalStorage();
      if (savedFilters) {
        if (savedFilters.searchTerm) {
          dispatch(setSearchTerm(savedFilters.searchTerm));
        }
        if (savedFilters.currentPage) {
          dispatch(setCurrentPage(savedFilters.currentPage));
        }
        if (savedFilters.perPage) {
          dispatch(setPerPage(savedFilters.perPage));
        }

        setFilters({
          status: savedFilters.status || { label: "", value: "" },
          region: savedFilters.region || "",
          category: savedFilters.category || "",
          subcategory: savedFilters.subcategory || "",
          product: savedFilters.product || "",
          brand: savedFilters.brand || "",
          initialized: true,
          fromUrl: false,
          fromLocalStorage: true,
        });

        setFiltersReady(true);
      } else {
        setFilters(prev => ({ ...prev, initialized: true, fromUrl: false, fromLocalStorage: false }));
        setFiltersReady(true);
      }
    }
  }

  useEffect(() => {
    if (filters.initialized && !filters.fromUrl && !filters.fromLocalStorage && !initialLoadRef.current && !isResettingRef.current) {
      saveFiltersToLocalStorage(filters);
    }
  }, [filters, saveFiltersToLocalStorage]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (filters.initialized && !initialLoadRef.current) {
        saveFiltersToLocalStorage(filters);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (filters.initialized && !initialLoadRef.current) {
        saveFiltersToLocalStorage(filters);
      }
    };
  }, [filters, saveFiltersToLocalStorage]);

  const buildQueryParams = useCallback((customParams = {}) => {
    const params = {
      page: currentPage,
      limit: perPage,
      search: searchTerm,
      status: filters.status?.value || '',
      region: filters.region,
      category: filters.category,
      subcategory: filters.subcategory,
      productId: filters.product,
      brand: filters.brand,
      ...customParams,
    };

    const filteredParams = Object.entries(params)
      .filter(([key, value]) => {
        if (value === '' || value === undefined || value === null) return false;
        if (key === 'status' && typeof value === 'object' && (!value || (value as any).value === '')) return false;
        return true;
      })
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    return filteredParams;
  }, [currentPage, perPage, searchTerm, filters]);

  const currentQueryString = useMemo(() => {
    const query = buildQueryParams();
    return query;
  }, [buildQueryParams]);

  useEffect(() => {
    const fn = async () => {
      try {
        const response = await getRegion(`page=1&limit=100`);
        if (response.data.status) {
          setAllNewRegion(response.data.data.data);
        } else {
          toast.error("Something went wrong");
        }
      } catch (error) {
        toast.error("Network Error");
      }
    };
    fn();
  }, []);

  const fetchData = useCallback(
    debounce(async () => {
      setFilterLoading(true);
      dispatch(setLoading(true));

      try {
        const response = await getAccessory(currentQueryString);
        if (response.data.status) {
          dispatch(setAccessory(response.data.data.data));
          dispatch(setTotalCount(Math.ceil(response.data.data.totalCount)));
          dispatch(setTotalPage(Math.ceil(response.data.data.totalCount / perPage)));
        } else {
          toast.error("Something went wrong");
        }
      } catch (error) {
        toast.error("Network Error");
      } finally {
        dispatch(setLoading(false));
        setInitload(false);
        setFilterLoading(false);
        initialLoadRef.current = false;
      }
    }, 300),
    [currentQueryString, dispatch, perPage, getAccessory]
  );

  useEffect(() => {
    if (!filters.initialized || isResettingRef.current) return;
    fetchData();
  }, [fetchData, filters.initialized , rerender1]);

  useEffect(() => {
    if (filters.fromUrl && !loading && data.length > 0 && !initialLoadRef.current) {
      saveFiltersToLocalStorage(filters);
      const timer = setTimeout(() => {
        setSearchParams({});
        setFilters(prev => ({ ...prev, fromUrl: false }));
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [filters.fromUrl, loading, data, setSearchParams, searchTerm, saveFiltersToLocalStorage]);

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('/accessory')) {
        dispatch(setSearchTerm(''));
      }
    };
  }, [dispatch]);

  useEffect(() => {
    isInitialMount.current = false;
  }, [filters.initialized, filtersReady, loading, initload]);

  const handleEditNavigation = useCallback((editUrl: string) => {
    saveFiltersToLocalStorage(filters);
    const navData = {
      fromBackButton: true,
      timestamp: Date.now(),
    };
    localStorage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(navData));
    navigate(editUrl);
  }, [filters, saveFiltersToLocalStorage, navigate]);

  const allIds = Array.isArray(data) ? data.map((item: any) => item._id) : [];
  const isAllSelected = selectedRows.length === allIds.length && allIds.length > 0;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allIds);
    }
  };

  const toggleRow = (id: string) => {
    setSelectedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const columns = [
    {
      name: "id",
      renderHeader: () => (
        <div className='form-check form-check-custom'>
          <input
            className='form-check-input'
            type='checkbox'
            data-kt-check={''}
            onChange={toggleSelectAll}
            checked={isAllSelected}
            style={{ margin: "0 3px" }}
          />
        </div>
      ),
      renderCell: (row: any) => (
        <div className='form-check form-check-custom'>
          <input
            className='form-check-input'
            type='checkbox'
            data-kt-check={''}
            style={{ margin: "0 3px" }}
            checked={selectedRows.includes(row._id)}
            onChange={() => toggleRow(row._id)}
          />
        </div>
      ),
    },
    {
      name: "image",
      renderHeader: (rows: any) => (
        <>
          <b>Image</b>
        </>
      ),
      renderCell: (row: any) => (
        <a
          href={`${process.env.REACT_APP_API_URL_FOR_IMAGE}${row && row.accessoryImages && row.accessoryImages[0]}`}
          target="_blank"
          style={{ height: "50px", width: "50px", display: "block", margin: "0 auto" }}
        >
          <img
            src={
              row && row?.accessoryImages && row?.accessoryImages[0]
                ? `${process.env.REACT_APP_API_URL_FOR_IMAGE}${row?.accessoryImages[0]}`
                : row && row?.storeMappedProduct?.[`0`]?.images?.[`0`]
            }
            style={{ height: "100%", width: "100%", objectFit: "cover" }}
          />
        </a>
      ),
    },
    {
      name: "name",
      renderHeader: (rows: any) => (
        <>
          <b>Name</b>
        </>
      ),
      renderCell: (row: any) => <>{row && row.accessoryName}</>,
    },
    {
      name: "modelNumber",
      renderHeader: (rows: any) => (
        <>
          <b>Model Number</b>
        </>
      ),
      renderCell: (row: any) => <>{row && row.modelNumber}</>,
    },
    {
      name: "brand",
      renderHeader: (rows: any) => (
        <>
          <b>Brand</b>
        </>
      ),
      renderCell: (row: any) => <>{row && row.brand}</>,
    },
    {
      name: "status",
      renderHeader: (rows: any) => (
        <>
          <b>Status</b>
        </>
      ),
      renderCell: (row: any) => (
        <Badge
          variant={row.status == "active" ? "success" : "danger"}
          className="fs-7 fw-bolder"
        >
          {row.status}
        </Badge>
      ),
    },
    {
      name: "subcategory",
      renderHeader: (rows: any) => (
        <>
          <b>Subcategory</b>
        </>
      ),
      renderCell: (row: any) => (
        <>
          {row?.subcategory && (
            <span>
              <a
                href={`/subcategory/edit?q=${row.subcategory._id}`}
                style={{ textDecoration: "underline" }}
                onClick={(e) => {
                  e.preventDefault();
                  handleEditNavigation(`/subcategory/edit?q=${row.subcategory._id}`);
                }}
              >
                {row.subcategory.subcategoryName}
              </a>
            </span>
          )}
        </>
      ),
    },
    {
      name: "regions",
      renderHeader: (rows: any) => (
        <>
          <b>Region</b>
        </>
      ),
      renderCell: (row: any) => (
        <>
          {row?.regions &&
            Array.prototype.map.call(row && row.regions, function (item) {
              return (
                <span key={item._id}>
                  <a
                    href={"/region/edit?q=" + item._id}
                    style={{ textDecoration: "underline" }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditNavigation(`/region/edit?q=${item._id}`);
                    }}
                  >
                    {item.regionName}
                  </a>
                  {"  "}
                </span>
              );
            })}
        </>
      ),
    },
    {
      name: "created_at",
      renderHeader: (rows: any) => (
        <>
          <b>Created At</b>
        </>
      ),
      renderCell: (row: any) => <>{row.createdAt}</>,
    },
    {
      name: "modified_at",
      renderHeader: (rows: any) => (
        <>
          <b>Modified At</b>
        </>
      ),
      renderCell: (row: any) => <>{row.updatedAt}</>,
    },
    {
      name: "Actions",
      renderHeader: (rows: any) => (
        <>
          <b>Actions</b>
        </>
      ),
      renderCell: (row: any) => (
        <AccessoryTableAction
          isAccessory={true}
          region={allNewRegion}
          accessory={row}
          currentFilters={currentQueryString}
          onEditNavigation={handleEditNavigation}
        />
      ),
    },
  ];

  const handlePageChange = useCallback((value: number) => {
    dispatch(setCurrentPage(value));
  }, [dispatch]);

  const handlePerPageChange = useCallback((value: number) => {
    dispatch(setPerPage(value));
  }, [dispatch]);

  const handleSearchParams = useCallback(
    debounce((value: string) => {
      if (value !== undefined && value !== '') {
        dispatch(setCurrentPage(1));
        dispatch(setSearchTerm(value));
      }
    }, 300),
    [dispatch]
  );

  const handleStatusFilter = useCallback((data: TOptionType<string>) => {
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, status: data };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleBrandFilter = useCallback((data: any) => {
    const payload = data.map((value: any) => value.brandName).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, brand: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleRegionFilter = useCallback((data: any) => {
    const payload = data.map((value: any) => value.id).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, region: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleCompatibiltyFilter = useCallback((data: any) => {
    const payload = data.map((value: any) => value._id).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, product: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleCategoryFilter = useCallback((data: any) => {
    const payload = data.map((value: any) => value._id).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, category: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleSubCategoryFilter = useCallback((data: any) => {
    const payload = data.map((value: any) => value._id).join(",");
    dispatch(setCurrentPage(1));
    setFilters(prev => {
      const newFilters = { ...prev, subcategory: payload };
      saveFiltersToLocalStorage(newFilters);
      return newFilters;
    });
  }, [dispatch, saveFiltersToLocalStorage]);

  const handleUpload = useCallback(() => {
    setUploadExcel(!uploadExcel);
  }, [uploadExcel]);

  const handleClearAllFilters = useCallback(() => {
    isResettingRef.current = true;
    setFilterLoading(true);

    localStorage.removeItem(FILTER_STORAGE_KEY);
    dispatch(setCurrentPage(1));
    dispatch(setPerPage(10));
    dispatch(setSearchTerm(""));

    setFilters({
      status: { label: "", value: "" },
      region: "",
      category: "",
      subcategory: "",
      product: "",
      brand: "",
      initialized: true,
      fromUrl: false,
      fromLocalStorage: false,
    });

    isResettingRef.current = false;
  }, [dispatch]);

  

  const configuration: TTableProps = {
    columns: columns,
    data: data,
    selectedRows: selectedRows,
    rows: data,
    showPagination: true,
    showSearch: true,
    showPerPage: true,
    searchValue: searchTerm || '',
    paginationData: {
      perPage: perPage,
      currentPage: currentPage,
      totalPage: totalPage,
      totalCount: totalCount,
    },
    showFilter: true,
    filters: (
      filtersReady ? (
        <>
          <div className="d-flex align-items-center justify-content-center gap-2">
            <AccessoryFilters
              key={`filters-${filters.initialized}-${filtersReady}`}
              onStatusFilterChange={handleStatusFilter}
              onRegionFilterChange={handleRegionFilter}
              currentRegionFilter={filters.region}
              currentStatusFilter={filters.status}
              onCategoryFilterChange={handleCategoryFilter}
              currentCategoryFilter={filters.category}
              onSubCategoryFilterChange={handleSubCategoryFilter}
              currentSubCategoryFilter={filters.subcategory}
              onAccessoryCompatibiltyFilterChange={handleCompatibiltyFilter}
              currentAccessoryCompatibilityFilter={filters.product}
              onBrandFilterChange={handleBrandFilter}
              currentBrandFilter={filters.brand}
            />
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleClearAllFilters}
                disabled={filterloading || !hasActiveFilters}
                style={{
                  fontSize: '12px',
                  padding: '12px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: (filterloading || !hasActiveFilters) ? "#ccc" : "#00A3FF",
                  color: "#fff",
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (filterloading || !hasActiveFilters) ? 'not-allowed' : 'pointer',
                  opacity: (filterloading || !hasActiveFilters) ? 0.6 : 1,
                }}
              >
                {filterloading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '16px', color: "#fff" }}></i>
                    
                  </>
                ) : (
                  <>
                    <i className="fas fa-times" style={{ fontSize: '12px', color: "#fff" }}></i>
                    Reset
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div>Loading filters...</div>
      )
    ),
    onPageChange: handlePageChange,
    onPerPageChange: handlePerPageChange,
    handleSearchParams: handleSearchParams,
    searchPlaceholder: "Search Accessories",
    searchLoading: filterloading,
  };

  const handleSelect = (selectedAction: string) => {
  // console.log('handleSelect called with:', selectedAction);
  // console.log('Current permissions:', userPermissions); 
  
  switch (selectedAction) {
    case "Manually":
      if (userPermissions.canAdd) {
        navigate("add");
      } 
      break;
    case "Upload-Excel":
      if (userPermissions.canAdd) {
        handleUpload();
      } 
      break;
    default:
      break;
  }
};

  // Show skeleton during initial mount, filter initialization, or data fetching
  if (isInitialMount.current || !filters.initialized || !filtersReady || (loading && initload)) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <>
      {showPermissionPrompt && (
        <PermissionPrompt
          title={"Access Denied"}
          image="/media/auth/permission-error-light.svg"
          message={"You don't have permission to add accessories"}
          // onCancel={handlePermissionCancel}
          moduleName="Accessory"
        />
      )}
      
      <div className="d-flex justify-content-between align-items-center mb-5">
        <div className="d-flex align-items-center" style={{ gap: "5px" }}>
          <h2>Accessories </h2>
          <img
            alt="Logo"
            src={toAbsoluteUrl("/media/icons/duotune/art/exclamation-icon.svg")}
            className="h-20px cursor-pointer"
            onClick={() => navigate(`/helps?moduleName=Accessory`)}
          />
        </div>
        {userPermissions.canAdd ? (
          <ActionCells
            actions={actions}
            onSelect={handleSelect}
            title={"Add Accessory"}
            buttonStyle={`btn btn-light btn-primary btn-xl`}
            iconStyle={`svg-icon-4 ms-1`}
          />
        ) : (
          <button
            type="button"
            className="btn btn-light btn-secondary btn-xl"
            // onClick={() => setShowPermissionPrompt(true)}
            style={{ cursor: 'not-allowed', opacity: 0.6 }}
          >
            {/* <i className="bi bi-plus-lg"></i> */}
            Add Accessory
          </button>
        )}
      </div>
      <AccessoryList {...configuration} />

      {uploadExcel && (
        <UploadAccessoryFormModal onClose={() => handleUpload()} />
      )}
    </>
  );
};

export default AccessoryPage;